import React from "react";
import { Button, Modal, Card, Container, Row, Col, Form, Spinner } from "react-bootstrap";
import { FaEye, FaFilePdf } from "react-icons/fa";
import { CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { config } from "../CommonUtils/CommonURLS";
import { failureAlert, successAlert2 } from "../CommonUtils/SweetAlerts";
import { useFormik, FormikProvider, ErrorMessage } from "formik";
import * as Yup from "yup";
import { viewImage } from "../CommonUtils/viewImage";
import { ImagePdfBucket } from "../CommonUtils/ImagesBase64";
import VerificationChecklistTable from "./VerificationChecklistTable";

export default function StudentsFormVerification({ selectedStudent, setShowCheckModal, fetchStudents, setSubmitting, verificationDetails }) {
  // Particulars List
  const particularsList = [
    { sno: 1, particular: "Fee Dues", asPerNorms: "Yes/No", fieldName: "feeDues", remarksField: "feeDuesRemarks" },
    { sno: 2, particular: "Attendance", asPerNorms: "Yes/No", fieldName: "attendance", remarksField: "attendanceRemarks" },
    { sno: 3, particular: "Disciplinary Issues", asPerNorms: "Yes/No", fieldName: "disciplinaryIssues", remarksField: "disciplinaryIssuesRemarks" },
    { sno: 4, particular: "Eligibility", asPerNorms: "Yes/No", fieldName: "eligibility", remarksField: "eligibilityRemarks" },
    { sno: 5, particular: "Detention History", asPerNorms: "Yes/No", fieldName: "detentionHistory", remarksField: "detentionHistoryRemarks" },
  ];
  const data = verificationDetails?.[0] || {};
  const [isFormLocked, setIsFormLocked] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);

  React.useEffect(() => {
    // Lock form when this student already has a saved verification decision.
    setIsFormLocked(Boolean(data?.decision));
  }, [data?.decision, selectedStudent?.student_reg_no]);
  const handleViewPdf = async () => {
    if (!selectedStudent?.student_reg_no) return;

    setPdfLoading(true);
    try {
      // const url = `${config.url.STU_URL}generate/${selectedStudent.student_reg_no}/fromCollege`;
      let url;
      console.log("Selected Student NOC Status:", selectedStudent.nocStatus);
      if (selectedStudent.noc_status === "Approved") {
        url = `${config.url.STU_URL}generate/${selectedStudent.student_reg_no}/fromCollege`;
      } else if (selectedStudent.noc_status === "Rejected") {
        url = `${config.url.STU_URL}generaterejection/${selectedStudent.student_reg_no}/fromCollege`;
      }
      const response = await CommonAxiosPost(url);

      if (response?.data?.status === true || response?.data?.scode === "01") {
        const base64Pdf = response?.data?.pdfBase64;
        if (base64Pdf) {
          const pdfWindow = window.open();
          pdfWindow.document.write(`
                    <iframe src="data:application/pdf;base64,${base64Pdf}" 
                            width="100%" height="100%" style="border:none;"></iframe>
                `);
        } else {
          failureAlert("No PDF data found");
        }
      } else {
        failureAlert(response?.data?.message || "Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
      failureAlert("Error generating PDF");
    } finally {
      setPdfLoading(false);
    }
  };
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      feeDues: data?.fee_dues || "",
      feeDuesRemarks: data?.fee_dues_remarks || "",
      attendance: data?.attendance || "",
      attendanceRemarks: data?.attendance_remarks || "",
      disciplinaryIssues: data?.disciplinary_issues || "",
      disciplinaryIssuesRemarks: data?.disciplinary_issues_remarks || "",
      eligibility: data?.eligibility || "",
      eligibilityRemarks: data?.eligibility_remarks || "",
      detentionHistory: data?.detention_history || "",
      detentionHistoryRemarks: data?.detention_history_remarks || "",
      decision: data?.decision || "",
      remarks: data?.remarks || "",
      nocApprovalLetter: data?.noc_approval_letter || "",
    },
    validationSchema: Yup.object({
      feeDues: Yup.string().required("Required"),
      feeDuesRemarks: Yup.string().required("Required"),

      attendance: Yup.string().required("Required"),
      attendanceRemarks: Yup.string().required("Required"),

      disciplinaryIssues: Yup.string().required("Required"),
      disciplinaryIssuesRemarks: Yup.string().required("Required"),

      eligibility: Yup.string().required("Required"),
      eligibilityRemarks: Yup.string().required("Required"),

      detentionHistory: Yup.string().required("Required"),
      detentionHistoryRemarks: Yup.string().required("Required"),

      decision: Yup.string().required("Required"),

      nocApprovalLetter: Yup.string().when("decision", {
        is: (decision) => !!decision,
        then: (schema) =>
          schema.required("NOC/Approval Letter is required").test("is-pdf", "File must be a valid PDF", (value) => {
            if (!value) return false;
            return typeof value === "string" && (value.endsWith(".pdf") || value.includes("pdf"));
          }),
        otherwise: (schema) => schema.notRequired(),
      }),

      remarks: Yup.string().when("decision", {
        is: (decision) => !!decision,
        then: (schema) => schema.required("Required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),

    onSubmit: async (values) => {
      if (isFormLocked) {
        return;
      }

      setSubmitting(true);
      const payload = {
        studentId: selectedStudent?.student_reg_no,
        ...values,
      };
      try {
        let endpoint = "StudentVerification";
        const response = await CommonAxiosPost(`${config.url.STU_URL}${endpoint}`, payload);
        if (response?.data?.status === true || response?.data?.scode === "01") {
          successAlert2(response?.data?.message || `${values.decision} submitted successfully`).then((result) => {
            if (result?.isConfirmed) {
              setShowCheckModal(false);
            }
          });
          formik.setValues(values);
          setIsFormLocked(true);
          if (typeof fetchStudents === "function") {
            fetchStudents();
          }
        } else {
          failureAlert(response?.data?.message || "Submission failed");
        }
      } catch (error) {
        console.error("Error submitting decision:", error);
        failureAlert("Something went wrong!");
      } finally {
        setSubmitting(false);
      }
    },
  });
  return (
    <FormikProvider value={formik}>
      <Form id="student-verification-form" onSubmit={formik.handleSubmit}>
        <Container fluid className="py-4">
          {/* PDF Download & Instructions */}
          {!isFormLocked && (
            <div
              className="mb-4 alert alert-info"
              style={{
                backgroundColor: "#e7f3ff",
                borderLeft: "4px solid #0d6efd",
              }}
            >
              <p className="mb-2 fw-bold text-primary">Kindly download the NOC Letter PDF and take a printout on your official letterhead.</p>

              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Button variant="success" size="sm" onClick={handleViewPdf} disabled={pdfLoading}>
                  <FaFilePdf className="me-1" />
                  {pdfLoading ? "Loading..." : "Please Click here to download the NOC Letter"}
                </Button>

                <span className="text-muted">
                  <strong className="text-danger">Note:</strong> Upload NOC/Approval Letter and provide remarks based on the verification checklist before making the final decision.
                </span>
              </div>
            </div>
          )}

          {/* Final Decision */}
          <h5 className="mb-3">Verification Checklist</h5>

          <VerificationChecklistTable particularsList={particularsList} formik={formik} disabled={isFormLocked} />

          {/* Final Decision */}
          <h5 className="mb-3 mt-4">Final Decision</h5>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="st-label">
                  {" "}
                  Decision <span className="text-danger">*</span>{" "}
                </Form.Label>
                <Form.Select
                  name="decision"
                  value={formik.values.decision}
                  onChange={(e) => {
                    formik.handleChange(e);
                    if (e.target.value !== "Reject") formik.setFieldValue("remarks", "");
                  }}
                  onBlur={formik.handleBlur}
                  className="mb-2"
                  disabled={isFormLocked}
                >
                  <option value="">-- Select Decision --</option>
                  <option value="Approve">Approve</option>
                  {/* <option value="Issue NOC">Issue Admission NOC</option> */}
                  <option value="Reject">Reject</option>
                </Form.Select>
                <ErrorMessage name="decision" component="div" className="text-danger" />
                {formik.values.decision === "Approve" && <Form.Text className="text-success">Application will be approved.</Form.Text>}
                {/* {formik.values.decision === 'Issue NOC' && <Form.Text className="text-info">Admission NOC will be issued to the student.</Form.Text>} */}
              </Form.Group>
            </Col>

            {formik.values.decision !== "" && (
              <>
                <Col xs={12} md={6} lg={4}>
                  <Form.Label className="st-label">
                    Upload NOC/Approval Letter
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="file"
                      name="nocApprovalLetter"
                      className="form-control st-control"
                      accept=".pdf"
                      disabled={isFormLocked}
                      onChange={(event) => {
                        const filename = "nocApprovalLetter";
                        const path = "/Uploads/StudentTransfer/";
                        ImagePdfBucket(event, formik, path, "nocApprovalLetter", filename, "pdf").then((uploadedPath) => {
                          formik.setFieldValue("nocApprovalLetter", uploadedPath || "");
                        });
                      }}
                    />
                    {formik.values.nocApprovalLetter && (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => viewImage(formik.values.nocApprovalLetter)}>
                        <FaEye />
                      </button>
                    )}
                  </div>
                  <ErrorMessage name="nocApprovalLetter" component="div" className="text-danger extra-small mt-1" />
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="st-label">
                      {" "}
                      Remarks <span className="text-danger">*</span>{" "}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="remarks"
                      rows={3}
                      placeholder="Please provide detailed reason"
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isFormLocked}
                    />
                    <ErrorMessage name="remarks" component="div" className="text-danger" />
                  </Form.Group>
                </Col>
              </>
            )}
          </Row>
        </Container>
      </Form>
    </FormikProvider>
  );
}
